import React, { useCallback, useEffect, useState, useRef, createElement } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { CustomCursor } from '../components/CustomCursor';
import { usePortfolio } from '../context/PortfolioContext';
import { ArrowRightIcon, CommandIcon, BriefcaseIcon, UploadCloudIcon, Loader2Icon, ZapIcon, LightbulbIcon, BarChart3Icon, CheckCircleIcon, AlertCircleIcon, FileTextIcon, XIcon, SparklesIcon, LinkIcon, ImageIcon, SearchIcon, FileIcon, ArrowLeftIcon, InfoIcon } from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import { analyzeJobDescription, analyzeJobDescriptionImage, analyzeJobDescriptionFromUrl, searchPortfolio } from '../utils/openaiService';
import { getTopMatchingCaseStudy, searchCaseStudies } from '../utils/caseStudyKeywords';
import { NoMatchModal } from '../components/NoMatchModal';
// Define the structure for our analyzed results
interface AnalysisResult {
  relevantCaseStudies: {
    slug: string;
    title: string;
    relevance: number;
    reason: string;
  }[];
  relevantSkills: {
    skill: string;
    category: string;
    relevance: number;
    description: string;
  }[];
  summary: string;
  jobTitle: string;
  companyName?: string;
  keyRequirements: string[];
  extractedKeywords?: string[];
  matchedRequirements?: {
    requirement: string;
    matchedWith: string;
  }[];
}
enum ViewState {
  PROMPT = 'prompt',
  PROCESSING = 'processing',
  RESULTS = 'results',
}
export function PromptHomePage() {
  const [prompt, setPrompt] = useState('');
  const [activeTab, setActiveTab] = useState('featured');
  const [isLoading, setIsLoading] = useState(false);
  const [showPlaceholder, setShowPlaceholder] = useState(true);
  const [viewState, setViewState] = useState<ViewState>(ViewState.PROMPT);
  const [isJobMode, setIsJobMode] = useState(false);
  const [jobResult, setJobResult] = useState<AnalysisResult | null>(null);
  const [error, setError] = useState('');
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [searchContext, setSearchContext] = useState(''); // NEW: Store what user is searching for
  const [showLinkedInWarning, setShowLinkedInWarning] = useState(false); // NEW: LinkedIn URL warning
  const loadingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const promptInputRef = useRef(null);
  const navigate = useNavigate();
  const [showNoMatchModal, setShowNoMatchModal] = useState(false);
  const [noMatchSearchTerm, setNoMatchSearchTerm] = useState('');
  const {
    userType,
    setViewPreference
  } = usePortfolio();
  const [searchResult, setSearchResult] = useState<any>(null);
  // Set view preference to 'ai' when this page loads
  useEffect(() => {
    setViewPreference('ai');
  }, [setViewPreference]);
  // Focus the prompt input when the component mounts
  useEffect(() => {
    if (promptInputRef.current) {
      promptInputRef.current.focus();
    }
  }, []);
  // Load UnicornStudio script
  useEffect(() => {
    if (!window.UnicornStudio) {
      window.UnicornStudio = {
        isInitialized: false
      };
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/gh/hiunicornstudio/unicornstudio.js@v1.4.29/dist/unicornStudio.umd.js';
      script.onload = () => {
        if (!window.UnicornStudio.isInitialized) {
          window.UnicornStudio.init();
          window.UnicornStudio.isInitialized = true;
        }
      };
      (document.head || document.body).appendChild(script);
    }
  }, []);
  const handlePromptChange = e => {
    const value = e.target.value;
    setPrompt(value);
    // Check if user is pasting a LinkedIn job URL
    if (isJobMode && value.includes('linkedin.com/jobs')) {
      setShowLinkedInWarning(true);
    } else {
      setShowLinkedInWarning(false);
    }
    if (value.length > 0) {
      setShowPlaceholder(false);
    } else {
      setShowPlaceholder(true);
    }
  };
  const handlePromptSubmit = async e => {
    e.preventDefault();
    const trimmedPrompt = prompt.trim();
    if (trimmedPrompt === '') return;
    console.log('🚀 Form submitted');
    console.log('📝 Input value:', trimmedPrompt);
    console.log('🎯 Mode:', isJobMode ? 'JOB MATCH' : 'SEARCH');
    // If we're in job mode, analyze the job description
    if (isJobMode) {
      console.log('✅ Routing to job analysis');
      await analyzeJobPrompt(trimmedPrompt);
      return;
    }
    // Otherwise, use AI search to find relevant case study
    console.log('✅ Routing to portfolio search');
    setIsLoading(true);
    setViewState(ViewState.PROCESSING);
    setSearchContext(trimmedPrompt);
    try {
      const result = await searchPortfolio(trimmedPrompt);
      setSearchResult(result);
      if (result.type === 'case-study' && result.caseStudy) {
        setTimeout(() => {
          navigate(`/case-study/${result.caseStudy.slug}`);
        }, 1500);
      } else if (result.type === 'skill' || result.type === 'general') {
        setViewState(ViewState.RESULTS);
        setIsLoading(false);
      } else {
        setError('No matching projects found. Try a different search.');
        setViewState(ViewState.PROMPT);
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Search error:', error);
      setError('Search failed. Please try again.');
      setViewState(ViewState.PROMPT);
      setIsLoading(false);
    }
  };
  const handleSuggestedTopic = (topic: string) => {
    setPrompt(topic);
    setShowPlaceholder(false);
    setShowNoMatchModal(false);
  };
  const analyzeJobPrompt = async jobDescription => {
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    console.log('🎯 JOB ANALYSIS STARTED');
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    console.log('📝 Input length:', jobDescription.length);
    console.log('📝 First 200 chars:', jobDescription.substring(0, 200));
    if (jobDescription.trim() === '') {
      setError('Please enter a job description or upload a file');
      return;
    }
    setError('');
    setIsLoading(true);
    setViewState(ViewState.PROCESSING);
    startProgressSimulation();
    try {
      // Smart detection: Check if input is a URL
      const trimmedInput = jobDescription.trim();
      let analysisResult: AnalysisResult;
      // Check for URL
      if (trimmedInput.startsWith('http://') || trimmedInput.startsWith('https://')) {
        console.log('🔗 INPUT TYPE: URL');
        console.log('🔗 URL:', trimmedInput);
        console.log('📤 Calling analyzeJobDescriptionFromUrl...');
        analysisResult = await analyzeJobDescriptionFromUrl(trimmedInput);
      }
      // Check for very short input (might be a mistake)
      else if (trimmedInput.length < 50) {
        console.log('⚠️ INPUT TYPE: Very short text (< 50 chars)');
        console.log('⚠️ This might not be a complete job description');
        console.log('📤 Proceeding with text analysis anyway...');
        analysisResult = await analyzeJobDescription(trimmedInput);
      }
      // Regular text input
      else {
        console.log('📝 INPUT TYPE: Text');
        console.log('📤 Calling analyzeJobDescription...');
        analysisResult = await analyzeJobDescription(trimmedInput);
      }
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.log('✅ ANALYSIS COMPLETE');
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.log('📊 Job Title:', analysisResult.jobTitle);
      console.log('🏢 Company:', analysisResult.companyName || 'Not specified');
      console.log('🔑 Keywords:', analysisResult.extractedKeywords?.length || 0);
      console.log('💼 Skills:', analysisResult.relevantSkills?.length || 0);
      console.log('📁 Case Studies:', analysisResult.relevantCaseStudies?.length || 0);
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      setJobResult(analysisResult);
      setIsLoading(false);
      stopProgressSimulation();
      setLoadingProgress(100);
      setTimeout(() => {
        setViewState(ViewState.RESULTS);
      }, 500);
    } catch (err) {
      console.error('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.error('❌ ANALYSIS FAILED');
      console.error('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.error('Error:', err);
      console.error('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      setError('Error analyzing job description. Please try again.');
      setIsLoading(false);
      stopProgressSimulation();
      setViewState(ViewState.PROMPT);
    }
  };
  const startProgressSimulation = () => {
    setLoadingProgress(0);
    loadingIntervalRef.current = setInterval(() => {
      setLoadingProgress(prev => {
        if (prev < 30) return prev + 2;
        if (prev < 60) return prev + 1;
        if (prev < 90) return prev + 0.5;
        return prev;
      });
    }, 100);
  };
  const stopProgressSimulation = () => {
    if (loadingIntervalRef.current) {
      clearInterval(loadingIntervalRef.current);
      loadingIntervalRef.current = null;
    }
  };
  const handleTabChange = tab => {
    setActiveTab(tab);
  };
  const toggleJobMode = () => {
    setIsJobMode(!isJobMode);
    setPrompt('');
    setShowPlaceholder(true);
    setViewState(ViewState.PROMPT);
    setJobResult(null);
    setError('');
    setUploadedFile(null);
  };
  const handleJobSuggestion = suggestion => {
    setPrompt(suggestion);
    setShowPlaceholder(false);
    analyzeJobPrompt(suggestion);
  };
  const resetForm = () => {
    setPrompt('');
    setUploadedFile(null);
    setShowPlaceholder(true);
    setJobResult(null);
    setError('');
    setViewState(ViewState.PROMPT);
  };
  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) return;
    const file = acceptedFiles[0];
    setUploadedFile(file);
    setError('');
    setViewState(ViewState.PROCESSING);
    startProgressSimulation();
    try {
      // Check if file is an image
      const isImage = file.type.startsWith('image/');
      if (isImage) {
        console.log('📸 Processing image file:', file.name);
        // Use image analysis for image files
        try {
          const analysisResult = await analyzeJobDescriptionImage(file);
          setJobResult(analysisResult);
          stopProgressSimulation();
          setLoadingProgress(100);
          setTimeout(() => {
            setViewState(ViewState.RESULTS);
          }, 500);
        } catch (analysisErr) {
          console.error('Error analyzing image:', analysisErr);
          setError('Error analyzing the image. Please try again with a clearer image or paste the text instead.');
          stopProgressSimulation();
          setViewState(ViewState.PROMPT);
        }
      } else {
        // Handle text files (PDF, TXT, DOCX)
        console.log('📄 Processing text file:', file.name);
        const text = await readFileAsText(file);
        setPrompt(text);
        setShowPlaceholder(false);
        try {
          const analysisResult = await analyzeJobDescription(text);
          // Set source type to 'file' for uploaded text files
          analysisResult.sourceType = 'file';
          analysisResult.sourceDetails = file.name;
          setJobResult(analysisResult);
          stopProgressSimulation();
          setLoadingProgress(100);
          setTimeout(() => {
            setViewState(ViewState.RESULTS);
          }, 500);
        } catch (analysisErr) {
          console.error('Error analyzing file content:', analysisErr);
          setError('Error analyzing the file content. Please try again.');
          stopProgressSimulation();
          setViewState(ViewState.PROMPT);
        }
      }
    } catch (err) {
      console.error('Error reading file:', err);
      setError(`Error reading file. Please try again.`);
      stopProgressSimulation();
      setViewState(ViewState.PROMPT);
    }
  }, []);
  const {
    getRootProps,
    getInputProps,
    isDragActive
  } = useDropzone({
    onDrop,
    accept: {
      'text/plain': ['.txt'],
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'image/png': ['.png'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/webp': ['.webp']
    }
  });
  const readFileAsText = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = e => {
        if (e.target?.result) {
          const fileText = e.target.result as string;
          if (file.type !== 'text/plain') {
            resolve(`[Note: This is a ${file.type} file. Text extraction may be incomplete.]\n\n${fileText}`);
          } else {
            resolve(fileText);
          }
        } else {
          reject(new Error('Failed to read file'));
        }
      };
      reader.onerror = () => reject(reader.error || new Error('File reading failed'));
      reader.readAsText(file);
    });
  };
  const removeFile = () => {
    setUploadedFile(null);
  };
  return <div className="w-full min-h-screen bg-black text-white" data-id="element-1205" data-mp="35l5zwdyuw269">
      <CustomCursor data-id="element-1206" data-mp="35nioh9z4dasa" />
      <Header data-id="element-1207" data-mp="30mbf56hbw3ji" />

      <div className="fixed bottom-5 left-5 z-50" data-id="element-1208" data-mp="30muo3xp03hle">
        <button onClick={() => navigate('/portfolio/traditional')} className="h-10 px-4 rounded-full flex items-center justify-center gap-2
              shadow-lg transition-all duration-300 
              bg-white/10 backdrop-blur-md text-white 
              border border-white/20 hover:bg-white/20 font-switzer" aria-label="Switch to traditional portfolio view" data-id="element-1209" data-mp="30m1qbm80s2fe">
          <SparklesIcon className="w-4 h-4" aria-hidden="true" data-id="element-1210" data-mp="2hwv1dhgmuel5" />
          <span className="text-sm font-medium" data-id="element-1211" data-mp="2cwh5exsb2v3h">AI Mode: ON</span>
        </button>
      </div>

      <section className="pt-32 pb-20 px-4 sm:px-6 md:px-8 relative overflow-hidden" data-id="element-1212" data-mp="27xnsb6gxl63u">
        {/* UnicornStudio Background - Updated Project ID */}
        <div className="aura-background-component top-0 w-full -z-10 absolute h-full" data-id="element-1213" data-mp="bvu5fsv3c1lz">
          <div data-us-project="4gq2Yrv2p0bIa0hdLPQx" className="absolute w-full h-full left-0 top-0 -z-10" data-id="element-1214" data-mp="gskhm53ryd24"></div>
        </div>

        <div className="max-w-4xl mx-auto relative z-10" data-id="element-1215" data-mp="24fyj00t7zit">
          <div className="mb-12 text-center" data-id="element-1216" data-mp="730wstwi494m">
            <h1 className="text-5xl sm:text-6xl md:text-7xl mb-6 text-white uppercase" style={{
            fontFamily: "'Gefika', sans-serif",
            fontWeight: 400,
            letterSpacing: '0.03em'
          }} data-id="element-1217" data-mp="71psmsz58zcz">
              AI-FOLIO
            </h1>
            <p className="text-lg sm:text-xl text-white/70 max-w-2xl mx-auto leading-relaxed" style={{
            fontFamily: "'Ronzino', sans-serif",
            fontWeight: 400
          }} data-id="element-1218" data-mp="3fc9gjbqv0byk">
              Quickly view my skills curated to your needs. Search for specific
              expertise or paste a job description to see if I'm a match.
            </p>
          </div>
          {viewState === ViewState.PROMPT && <div data-id="element-1219" data-mp="331i7cxs2giq4">
              {/* Two buttons above search bar - UPDATED styling */}
              <div className="flex gap-3 mb-4 items-center" data-id="element-1220" data-mp="2y38bsm2fjg3z">
                <button onClick={() => setIsJobMode(false)} className={`relative group px-4 py-2 rounded-lg border transition-all ${!isJobMode ? 'bg-white/10 border-white/30 text-white backdrop-blur-md' : 'bg-transparent border-white/10 text-white/50 hover:text-white/80 hover:bg-white/5'}`} data-id="element-1221" data-mp="3cqjowse9f867">
                  <div className="flex items-center gap-2" data-id="element-1222" data-mp="3gsncugi4858d">
                    <SearchIcon className="w-4 h-4" data-id="element-1223" data-mp="3gs58ewlaukpd" />
                    <span className="text-sm font-medium" data-id="element-1224" data-mp="3vq7bz6sbz66m">Search</span>
                  </div>
                </button>

                <button onClick={() => setIsJobMode(true)} className={`relative px-4 py-2 rounded-lg border transition-all ${isJobMode ? 'bg-white/10 border-white/30 text-white backdrop-blur-md' : 'bg-transparent border-white/10 text-white/50 hover:text-white/80 hover:bg-white/5'}`} data-id="element-1225" data-mp="3qrmcxaotesjr">
                  <div className="flex items-center gap-1" data-id="element-1226" data-mp="2m1h3lzqczerr">
                    <FileIcon className="w-3 h-3" data-id="element-1227" data-mp="2m31xro8rgvp3" />
                    <span className="text-xs font-medium" data-id="element-1228" data-mp="2q3c4j34dnq50">Role Match</span>
                  </div>
                </button>

                {/* InfoIcon - positioned to the right of Role Match button */}
                <div className="relative group" data-id="element-1229" data-mp="3019q1iuur8tx">
                  <InfoIcon className="w-4 h-4 text-white/50 hover:text-white/80 transition-colors cursor-help" data-id="element-1230" data-mp="34zjj9nx0nzcw" />
                  {/* Tooltip - positioned to the right */}
                  <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50 w-64" data-id="element-1231" data-mp="38zbu679vuhlf">
                    <div className="bg-white text-black text-xs px-4 py-3 rounded-lg shadow-xl border border-gray-200" data-id="element-1232" data-mp="3iqgc9fuuuikh">
                      <p className="leading-relaxed" data-id="element-1233" data-mp="3nnyc8gqw9ngc">
                        Have a specific role in mind? Paste the job requirements
                        here, and my AI will scan my entire portfolio and curate
                        all relevant experience, projects, and skills that match
                        your needs.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Search input area - UPDATED styling */}
              <form onSubmit={handlePromptSubmit} className="mb-4" data-id="element-1234" data-mp="126w538ra77f2">
                <div className="relative" data-id="element-1235" data-mp="1zrqrmjthek48">
                  <div className="flex items-center bg-black/40 backdrop-blur-xl border border-white/10 shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] rounded-lg p-4 focus-within:border-white/30 focus-within:bg-black/60 transition-all duration-300" data-id="element-1236" data-mp="1ut7269foiq74">
                    {isJobMode ? <div className="flex items-center gap-2 mr-3" data-id="element-1237" data-mp="1l341ro68a9yr">
                        <div {...getRootProps()} className="cursor-pointer relative group" data-id="element-1238" data-mp="2ijtck7d5omka">
                          <input {...getInputProps()} aria-label="Upload job description file or image" className="sr-only" data-id="element-1239" data-mp="2drt4frmxmncm" />
                          {uploadedFile ? <div className="relative" data-id="element-1240" data-mp="1m36v66mykw15">
                              <FileTextIcon className="h-5 w-5 text-blue-400" data-id="element-1241" data-mp="1qvgirg0lpk09" />
                              <button type="button" onClick={e => {
                        e.stopPropagation();
                        removeFile();
                      }} className="absolute -top-1 -right-1 bg-black rounded-full p-0.5" data-id="element-1242" data-mp="1d421bdmcdqvb">
                                <XIcon className="h-3 w-3" data-id="element-1243" data-mp="25sz6cf1jlr64" />
                              </button>
                            </div> : isDragActive ? <UploadCloudIcon className="h-5 w-5 text-white/70 animate-pulse" data-id="element-1244" data-mp="29ryjgmxfgu8n" /> : <>
                              <UploadCloudIcon className="h-5 w-5 text-white/50 hover:text-white/80 transition-colors" data-id="element-1245" data-mp="1vwdzdo5yq5zn" />
                              <div className="absolute opacity-0 group-hover:opacity-100 transition-opacity duration-200 bottom-full mb-2 left-1/2 -translate-x-1/2 bg-white text-black text-xs px-3 py-1.5 rounded-lg whitespace-nowrap z-10 shadow-lg" data-id="element-1246" data-mp="kf4jfbm1lw33">
                                Upload PDF or screenshot
                              </div>
                            </>}
                        </div>
                        <div className="h-5 w-px bg-white/20" data-id="element-1247" data-mp="qveiyde69cre"></div>
                      </div> : <CommandIcon className="h-5 w-5 text-white/50 mr-3" data-id="element-1248" data-mp="qtacpossw1ny" />}
                    {isJobMode ? <input ref={promptInputRef} type="text" value={prompt} onChange={handlePromptChange} className="flex-grow bg-transparent text-white outline-none placeholder-white/30" placeholder="Paste job description or public URL..." disabled={isLoading} data-id="element-1249" data-mp="lx34tqgugg3d" /> : <input ref={promptInputRef} type="text" value={prompt} onChange={handlePromptChange} className="flex-grow bg-transparent text-white outline-none placeholder-white/30" placeholder="Search projects or ask about skills..." disabled={isLoading} data-id="element-1250" data-mp="7x25wgk53iqj" />}
                    <button type="submit" className="ml-2 p-2 rounded-md bg-white/10 hover:bg-white/20 transition-colors disabled:opacity-50" disabled={isLoading || !prompt.trim()} data-id="element-1251" data-mp="3bjix0j1ywjds">
                      {isLoading ? <div className="h-5 w-5 border-2 border-white/50 border-t-white rounded-full animate-spin" data-id="element-1252" data-mp="2309x5i16uxkx"></div> : <ArrowRightIcon className="h-5 w-5 text-white" data-id="element-1253" data-mp="1y16qjt5k8jau" />}
                    </button>
                  </div>

                  {/* LinkedIn Warning Message */}
                  {showLinkedInWarning && <div className="mt-3 bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-4" data-id="element-1254" data-mp="27zcpq2zzfbc0">
                      <div className="flex items-start gap-3" data-id="element-1255" data-mp="2lux90zjmhvjy">
                        <AlertCircleIcon className="h-5 w-5 text-yellow-400 mt-0.5 flex-shrink-0" data-id="element-1256" data-mp="2hqh4b1lqwu6b" />
                        <div data-id="element-1257" data-mp="2vpp8k8wrmshd">
                          <p className="text-yellow-200 text-sm font-medium mb-2" data-id="element-1258" data-mp="2voxl5ldbo126">
                            LinkedIn job links require authentication
                          </p>
                          <p className="text-yellow-200/80 text-xs mb-3" data-id="element-1259" data-mp="11disowsxfniq">
                            I can't access LinkedIn job postings directly. For
                            best results:
                          </p>
                          <div className="space-y-1.5 text-xs text-yellow-200/80" data-id="element-1260" data-mp="383lx3aedy98c">
                            <div className="flex items-center gap-2" data-id="element-1261" data-mp="3d2faz3xl4291">
                              <CheckCircleIcon className="h-3.5 w-3.5 text-yellow-400" data-id="element-1262" data-mp="2yaokfkw2rehn" />
                              <span data-id="element-1263" data-mp="339hokmph1t55">
                                Take a screenshot of the job posting and upload
                                it
                              </span>
                            </div>
                            <div className="flex items-center gap-2" data-id="element-1264" data-mp="2ubfqaex4helk">
                              <CheckCircleIcon className="h-3.5 w-3.5 text-yellow-400" data-id="element-1265" data-mp="2fddooa9nj36y" />
                              <span data-id="element-1266" data-mp="2kb5duwiwwb7g">
                                Copy and paste the job description text
                              </span>
                            </div>
                            <div className="flex items-center gap-2" data-id="element-1267" data-mp="ikbqhn2ompow">
                              <CheckCircleIcon className="h-3.5 w-3.5 text-yellow-400" data-id="element-1268" data-mp="ijisbahsh8id" />
                              <span data-id="element-1269" data-mp="h4pta348fca4">
                                Use public job board URLs (Greenhouse, Lever,
                                Workday)
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>}

                  {error && <div className="flex items-center text-red-400 text-sm mt-2" data-id="element-1270" data-mp="ztcx6iao9vm4">
                      <AlertCircleIcon className="h-4 w-4 mr-2" data-id="element-1271" data-mp="uujkgs37m8n0" />
                      {error}
                    </div>}
                </div>
              </form>

              {/* Helper text below search bar - Updated for Role Match */}
              {isJobMode && !showLinkedInWarning && <div className="mb-8 text-center" data-id="element-1272" data-mp="m1zl1pf3g014">
                  <p className="text-white/50 text-xs" data-id="element-1273" data-mp="1ikdw56bslr5o">
                    💡 Best results: Upload PDF/screenshot or paste full job
                    description text
                  </p>
                </div>}

              {/* File upload display */}
              {isJobMode && uploadedFile && <div className="bg-white/5 border border-white/20 rounded-lg p-4 flex items-center justify-between mb-8" data-id="element-1274" data-mp="31eyzftg43ioa">
                  <div className="flex items-center" data-id="element-1275" data-mp="35fsjgbqfodbw">
                    <FileTextIcon className="h-5 w-5 text-white/50 mr-3" data-id="element-1276" data-mp="35j6kngnd2ghr" />
                    <div data-id="element-1277" data-mp="3ah87mcnwqzyi">
                      <p className="text-white font-medium" data-id="element-1278" data-mp="3ff1bgz1qnhvm">
                        {uploadedFile.name}
                      </p>
                      <p className="text-white/50 text-xs" data-id="element-1279" data-mp="3kdb5357teaf4">
                        {(uploadedFile.size / 1024).toFixed(1)} KB
                      </p>
                    </div>
                  </div>
                  <button type="button" onClick={removeFile} className="text-white/70 hover:text-white" data-id="element-1280" data-mp="3t6o8j13i8ufg">
                    <XIcon className="h-4 w-4" data-id="element-1281" data-mp="216co1ht2b45" />
                  </button>
                </div>}
              {/* Suggestions - only show in Search mode - UPDATED styling */}
              {!isJobMode && <div className="mb-8" data-id="element-1282" data-mp="3ph3nrn9rcy9x">
                  <h3 className="text-sm uppercase text-white/50 mb-4" data-id="element-1283" data-mp="3pg2cbhoty9ro">
                    Quick prompts:
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-1284" data-mp="32arkq9w6i8u">
                    <button onClick={async () => {
                const query = 'Do you have financial product design experience?';
                setPrompt(query);
                setShowPlaceholder(false);
                setSearchContext(query);
                setIsLoading(true);
                setViewState(ViewState.PROCESSING);
                try {
                  const result = await searchPortfolio(query);
                  if (result.type === 'case-study' && result.caseStudy) {
                    setTimeout(() => {
                      navigate(`/case-study/${result.caseStudy.slug}`);
                    }, 1500);
                  }
                } catch (error) {
                  console.error('Search error:', error);
                  setError('Search failed. Please try again.');
                  setViewState(ViewState.PROMPT);
                  setIsLoading(false);
                }
              }} className="bg-transparent border border-white/10 rounded-md px-4 py-3 text-sm text-left hover:border-white/30 hover:bg-white/5 transition-all text-white/80" data-id="element-1285" data-mp="34dgt83807ci">
                      "Do you have financial product design experience?"
                    </button>
                    <button onClick={async () => {
                const query = 'Any experience designing data heavy screens?';
                setPrompt(query);
                setShowPlaceholder(false);
                setSearchContext(query);
                setIsLoading(true);
                setViewState(ViewState.PROCESSING);
                try {
                  const result = await searchPortfolio(query);
                  if (result.type === 'case-study' && result.caseStudy) {
                    setTimeout(() => {
                      navigate(`/case-study/${result.caseStudy.slug}`);
                    }, 1500);
                  }
                } catch (error) {
                  console.error('Search error:', error);
                  setError('Search failed. Please try again.');
                  setViewState(ViewState.PROMPT);
                  setIsLoading(false);
                }
              }} className="bg-transparent border border-white/10 rounded-md px-4 py-3 text-sm text-left hover:border-white/30 hover:bg-white/5 transition-all text-white/80" data-id="element-1286" data-mp="1h3ysnell0a2y">
                      "Any experience designing data heavy screens?"
                    </button>
                  </div>
                </div>}
            </div>}
          {/* Processing/Loading screen */}
          {viewState === ViewState.PROCESSING && <div className="max-w-xl mx-auto text-center" data-id="element-1287" data-mp="1cxtm5c06u5h9">
              <div className="mb-8" data-id="element-1288" data-mp="1cxbe7i45y2ti">
                <div className="inline-flex items-center justify-center p-4 bg-white/5 rounded-full mb-6" data-id="element-1289" data-mp="17z9xv4y4ghlq">
                  <Loader2Icon className="h-10 w-10 text-white animate-spin" data-id="element-1290" data-mp="xvl2aq8shbsw" />
                </div>
                <h2 className="text-xl sm:text-2xl mb-3" style={{
              fontFamily: "'Gefika', sans-serif",
              fontWeight: 400
            }} data-id="element-1291" data-mp="12tmwo7bo0xjo">
                  {isJobMode ? 'Analyzing Job Description' : 'Scanning Portfolio'}
                </h2>
                <p className="text-white/70 text-lg mb-2" data-id="element-1292" data-mp="1cpzpz43at9dw">
                  {isJobMode ? 'AI is scanning my entire portfolio and resume to match your requirements with relevant case studies, skills, and experience...' : searchContext ? `AI is searching through all case studies for projects matching "${searchContext}"` : 'AI is scanning through portfolio for the best match...'}
                </p>
                {!isJobMode && searchResult?.caseStudy && <p className="text-white/50 text-sm mt-4" data-id="element-1293" data-mp="3sk9qko230x9n">
                    Found: {searchResult.caseStudy.title}
                  </p>}
              </div>
              {isJobMode && <>
                  <div className="w-full bg-white/5 rounded-full h-2 mb-8" data-id="element-1294" data-mp="1fj3z9aoi84u6">
                    <div className="bg-white h-2 rounded-full transition-all duration-300 ease-out" style={{
                width: `${loadingProgress}%`
              }} data-id="element-1295" data-mp="1bi1xon747gp1"></div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-1296" data-mp="1kiqcvl88vn3i">
                    <div className="bg-white/5 border border-white/10 rounded-lg p-4" data-id="element-1297" data-mp="1kktbgtngqoju">
                      <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center mb-3 mx-auto" data-id="element-1298" data-mp="1ygonqgncx1e9">
                        <span className="text-sm font-medium" data-id="element-1299" data-mp="1uf3ct0udux6p">1</span>
                      </div>
                      <p className="text-sm text-white/70" data-id="element-1300" data-mp="28a5h5jjl0ehe">
                        Scanning all case studies
                      </p>
                    </div>
                    <div className="bg-white/5 border border-white/10 rounded-lg p-4" data-id="element-1301" data-mp="2i7a6ttcmys2y">
                      <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center mb-3 mx-auto" data-id="element-1302" data-mp="2d8grdvdsgr05">
                        <span className="text-sm font-medium" data-id="element-1303" data-mp="2wtp9porc7zuo">2</span>
                      </div>
                      <p className="text-sm text-white/70" data-id="element-1304" data-mp="30u98ewrk7bix">
                        Matching skills & experience
                      </p>
                    </div>
                    <div className="bg-white/5 border border-white/10 rounded-lg p-4" data-id="element-1305" data-mp="2rtl2kpcbkhh4">
                      <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center mb-3 mx-auto" data-id="element-1306" data-mp="2d5gjhk9cu3xt">
                        <span className="text-sm font-medium" data-id="element-1307" data-mp="2hw6mwry7zwpv">3</span>
                      </div>
                      <p className="text-sm text-white/70" data-id="element-1308" data-mp="245bd9dfwdbl8">
                        Curating relevant projects
                      </p>
                    </div>
                  </div>
                </>}
            </div>}
          {/* Results section */}
          {viewState === ViewState.RESULTS && jobResult && <div className="max-w-4xl mx-auto" data-id="element-1309" data-mp="1z5p01402jvcf">
              {/* Back Button - NEW */}
              <div className="mb-8" data-id="element-1310" data-mp="1v1jaj58dac43">
                <button onClick={resetForm} className="inline-flex items-center gap-2 text-white/70 hover:text-white transition-colors group" aria-label="Back to job matcher" data-id="element-1311" data-mp="1v2kp3joldgqk">
                  <ArrowLeftIcon className="w-4 h-4 transition-transform group-hover:-translate-x-1" data-id="element-1312" data-mp="24y4gptw4ht9i" />
                  <span className="text-sm font-medium" data-id="element-1313" data-mp="291u7mq09smxp">
                    Back to Job Matcher
                  </span>
                </button>
              </div>

              {/* Extracted Job Information Section - NEW */}
              <div className="mb-8" data-id="element-1314" data-mp="2ix49ndqo4ubh">
                <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-6" data-id="element-1315" data-mp="2npxe00i860n1">
                  <div className="flex items-start mb-4" data-id="element-1316" data-mp="2sos1hfr50ddh">
                    <FileTextIcon className="h-5 w-5 text-blue-400 mt-1 mr-3 flex-shrink-0" data-id="element-1317" data-mp="2so8idvpr3ixv" />
                    <div className="flex-grow" data-id="element-1318" data-mp="2wry7cmac82jf">
                      <h3 className="text-lg font-semibold text-blue-300 mb-2" data-id="element-1319" data-mp="31p6rgsf1qgnb">
                        Job Description Analysis
                      </h3>

                      {/* Source Information - NEW */}
                      {jobResult.sourceType && <div className="mb-4 pb-4 border-b border-blue-500/20" data-id="element-1320" data-mp="l28hxd2t6wag">
                          <div className="flex items-center gap-2 text-sm text-blue-200/80" data-id="element-1321" data-mp="h0myt9zollrt">
                            {jobResult.sourceType === 'url' && <>
                                <LinkIcon className="w-4 h-4" data-id="element-1322" data-mp="c4ocdrxwydu1" />
                                <span data-id="element-1323" data-mp="18s2udtj7uiu6">Analyzed from URL:</span>
                              </>}
                            {jobResult.sourceType === 'image' && <>
                                <ImageIcon className="w-4 h-4" data-id="element-1324" data-mp="zzhd6hv8gk7g" />
                                <span data-id="element-1325" data-mp="zxxwwr9ku95o">Analyzed from image:</span>
                              </>}
                            {jobResult.sourceType === 'file' && <>
                                <FileIcon className="w-4 h-4" data-id="element-1326" data-mp="1si4s1fjmz303" />
                                <span data-id="element-1327" data-mp="1npbsd630ykuv">Analyzed from file:</span>
                              </>}
                            {jobResult.sourceType === 'text' && <>
                                <FileTextIcon className="w-4 h-4" data-id="element-1328" data-mp="26ogadgqs93j9" />
                                <span data-id="element-1329" data-mp="2bkovdarnvqyc">Analyzed from pasted text</span>
                              </>}
                          </div>
                          {jobResult.sourceDetails && <p className="text-xs text-blue-200/60 mt-1 break-all" data-id="element-1330" data-mp="21uvgo0w82vpv">
                              {jobResult.sourceDetails}
                            </p>}
                        </div>}

                      <div className="space-y-3" data-id="element-1331" data-mp="1sv8hzmuvo40i">
                        <div data-id="element-1332" data-mp="1sw0a2np9njm1">
                          <span className="text-white/50 text-sm" data-id="element-1333" data-mp="1ev7f61v1ymie">
                            Position:
                          </span>
                          <p className="text-white font-medium text-lg" data-id="element-1334" data-mp="1ixasyx57236u">
                            {jobResult.jobTitle}
                          </p>
                        </div>
                        {jobResult.companyName && <div data-id="element-1335" data-mp="3d98zusg16mzk">
                            <span className="text-white/50 text-sm" data-id="element-1336" data-mp="3d87rwwubagm2">
                              Company:
                            </span>
                            <p className="text-white font-medium" data-id="element-1337" data-mp="xmeoh7ehf20l">
                              {jobResult.companyName}
                            </p>
                          </div>}
                        {jobResult.extractedKeywords && jobResult.extractedKeywords.length > 0 && <div data-id="element-1338" data-mp="16kgslvtiias3">
                              <span className="text-white/50 text-sm" data-id="element-1339" data-mp="1laensvpv00qs">
                                Key Terms Found:
                              </span>
                              <div className="flex flex-wrap gap-2 mt-2" data-id="element-1340" data-mp="1ge7in55aet9w">
                                {jobResult.extractedKeywords.map((keyword, index) => <span key={index} className="bg-blue-500/20 text-blue-200 px-3 py-1 rounded-full text-xs border border-blue-500/30" data-id="element-1341" data-mp="1pd19sfzumrg2" data-mpi={index}>
                                      {keyword}
                                    </span>)}
                              </div>
                            </div>}
                      </div>
                    </div>
                  </div>
                  <p className="text-white/60 text-sm italic" data-id="element-1342" data-mp="1f0gky89ittb">
                    ✓ Successfully analyzed your job description and matched it
                    with relevant experience
                  </p>
                </div>
              </div>

              {/* Matched Requirements Section - NEW */}
              {jobResult.matchedRequirements && jobResult.matchedRequirements.length > 0 && <div className="mb-16" data-id="element-1343" data-mp="39sr02930xbw5">
                    <h2 className="text-2xl sm:text-3xl mb-6 flex items-center uppercase" style={{
              fontFamily: "'Gefika', sans-serif",
              fontWeight: 400,
              letterSpacing: '0.02em'
            }} data-id="element-1344" data-mp="3et57pq0pqjtn">
                      <CheckCircleIcon className="h-6 w-6 mr-3 text-green-400" data-id="element-1345" data-mp="3olawkqvo4l4m" />
                      How I Match Your Requirements
                    </h2>
                    <div className="space-y-4" data-id="element-1346" data-mp="2w2vt1gnqozl2">
                      {jobResult.matchedRequirements.map((match, index) => <div key={index} className="bg-white/5 border border-white/20 rounded-lg p-5 hover:border-white/40 transition-colors" data-id="element-1347" data-mp="2okfsqv1o02hb" data-mpi={index}>
                          <div className="flex items-start gap-4" data-id="element-1348" data-mp="2jll1r5tjn7m4">
                            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center" data-id="element-1349" data-mp="2ekp0z14ho1kx">
                              <CheckCircleIcon className="h-5 w-5 text-green-400" data-id="element-1350" data-mp="29mdrwsm0pv3r" />
                            </div>
                            <div className="flex-grow" data-id="element-1351" data-mp="25mv6lvqabi0e">
                              <p className="text-white font-medium mb-2" data-id="element-1352" data-mp="20tjvvjicqh37">
                                "{match.requirement}"
                              </p>
                              <p className="text-white/70 text-sm" data-id="element-1353" data-mp="1qwe4ea5tso2g">
                                → {match.matchedWith}
                              </p>
                            </div>
                          </div>
                        </div>)}
                    </div>
                  </div>}

              {/* Summary section */}
              <div className="mb-16" data-id="element-1354" data-mp="179aw2o25ewwz">
                <div className="bg-white/5 border border-white/20 rounded-lg p-8" data-id="element-1355" data-mp="17cgp2edweixz">
                  <div className="flex items-center mb-6" data-id="element-1356" data-mp="1caqj2lnvzdi0">
                    <div className="bg-white/10 p-3 rounded-full mr-4" data-id="element-1357" data-mp="yafs7imktywu">
                      <BarChart3Icon className="h-6 w-6 text-white" data-id="element-1358" data-mp="138r3lxsishh6" />
                    </div>
                    <div data-id="element-1359" data-mp="1qybledcxt8ov">
                      <h2 className="text-2xl uppercase" style={{
                    fontFamily: "'Gefika', sans-serif",
                    fontWeight: 400,
                    letterSpacing: '0.02em'
                  }} data-id="element-1360" data-mp="1qy37qj8zpzcm">
                        {jobResult.jobTitle} Match Analysis
                      </h2>
                      <p className="text-white/70" data-id="element-1361" data-mp="1m6l8bzmm86tr">
                        Here's how my experience aligns with this role
                      </p>
                    </div>
                  </div>
                  <p className="text-lg leading-relaxed mb-8" data-id="element-1362" data-mp="2eoz7c2jotyuf">
                    {jobResult.summary}
                  </p>
                  <div className="mb-8" data-id="element-1363" data-mp="2o16bazf8op2h">
                    <h3 className="text-lg font-semibold mb-4" data-id="element-1364" data-mp="2k1e1yki0uew2">
                      Key Requirements
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-1365" data-mp="2xrhicuu7zedl">
                      {jobResult.keyRequirements.map((req, index) => <div key={index} className="flex items-start" data-id="element-1366" data-mp="2stzhzsu9q7h7" data-mpi={index}>
                          <CheckCircleIcon className="h-5 w-5 text-green-400 mt-0.5 mr-2 flex-shrink-0" data-id="element-1367" data-mp="2533makmoys4o" />
                          <span data-id="element-1368" data-mp="213biel88ta5n">{req}</span>
                        </div>)}
                    </div>
                  </div>
                  <button onClick={resetForm} className="text-white/70 hover:text-white text-sm flex items-center transition-colors" data-id="element-1369" data-mp="2f3mafrlaguse">
                    Analyze another job description
                    <ArrowRightIcon className="h-4 w-4 ml-1" data-id="element-1370" data-mp="3qfwu98i21gkp" />
                  </button>
                </div>
              </div>
              {/* Relevant skills section */}
              <div className="mb-16" data-id="element-1371" data-mp="1jtqt072s3btc">
                <h2 className="text-2xl sm:text-3xl mb-8 flex items-center uppercase" style={{
              fontFamily: "'Gefika', sans-serif",
              fontWeight: 400,
              letterSpacing: '0.02em'
            }} data-id="element-1372" data-mp="1jushbd8t1ut0">
                  <ZapIcon className="h-6 w-6 mr-3 text-yellow-400" data-id="element-1373" data-mp="1xkcdg07vrttc" />
                  Matching Skills
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1374" data-mp="22jyuyluqrw7y">
                  {jobResult.relevantSkills.map((skill, index) => <div key={index} className="bg-white/5 border border-white/20 rounded-lg p-6 hover:border-white/40 transition-colors" data-id="element-1375" data-mp="27h7byj45lu7m" data-mpi={index}>
                      <div className="flex justify-between items-start mb-3" data-id="element-1376" data-mp="2biar13ednm7s">
                        <h3 className="text-xl font-semibold" data-id="element-1377" data-mp="2gifa916o6v1p">{skill.skill}</h3>
                        <span className="bg-white/10 px-2 py-1 rounded text-sm" data-id="element-1378" data-mp="h7e4y4acz39z">
                          {Math.round(skill.relevance * 100)}% match
                        </span>
                      </div>
                      <p className="text-white/70 mb-2" data-id="element-1379" data-mp="en93kyd1iw9o">{skill.description}</p>
                      <span className="text-xs uppercase tracking-wider text-white/50" data-id="element-1380" data-mp="9twszs574pzx">
                        {skill.category}
                      </span>
                    </div>)}
                </div>
              </div>
              {/* Relevant case studies section */}
              <div data-id="element-1381" data-mp="xkaaioq5n6g9">
                <h2 className="text-2xl sm:text-3xl mb-8 flex items-center uppercase" style={{
              fontFamily: "'Gefika', sans-serif",
              fontWeight: 400,
              letterSpacing: '0.02em'
            }} data-id="element-1382" data-mp="xksde47boix5">
                  <LightbulbIcon className="h-6 w-6 mr-3 text-blue-400" data-id="element-1383" data-mp="nkjfmlxompfn" />
                  Relevant Case Studies
                </h2>
                <div className="space-y-8" data-id="element-1384" data-mp="38clle1m4fvxj">
                  {jobResult.relevantCaseStudies.map((study, index) => <div key={index} className="border border-white/20 rounded-lg overflow-hidden bg-white/5" data-id="element-1385" data-mp="34b01vxf30jed" data-mpi={index}>
                      <div className="p-6" data-id="element-1386" data-mp="2wr8uzklgvp1g">
                        <div className="flex flex-col md:flex-row md:items-start gap-6" data-id="element-1387" data-mp="2wqz1vo54l1rj">
                          <div className="md:w-1/3 h-48 overflow-hidden rounded-lg" data-id="element-1388" data-mp="2rtpcu9lnes48">
                            <img src={study.slug === 'us-bank' ? '/usbthumb.png' : study.slug === 'cyber-security' ? '/unisys_thumb.png' : '/Gemini_Generated_Image_qq6ptsqq6ptsqq6p.png'} alt={study.title} className="w-full h-full object-cover transition-transform duration-500 hover:scale-105" data-id="element-1389" data-mp="2nprfo4fvvrdt" />
                          </div>
                          <div className="md:w-2/3" data-id="element-1390" data-mp="290li0kwp2981">
                            <div className="flex justify-between items-start mb-4" data-id="element-1391" data-mp="242bn8bevtamy">
                              <h3 className="text-xl font-semibold" data-id="element-1392" data-mp="201hxrdlsjnry">
                                {study.title}
                              </h3>
                              <span className="bg-white/10 px-2 py-1 rounded text-sm" data-id="element-1393" data-mp="1v1lu5fh09ycx">
                                {Math.round(study.relevance * 100)}% relevant
                              </span>
                            </div>
                            <div className="mb-4" data-id="element-1394" data-mp="5ndxdobmtg25">
                              <p className="text-white/70 mb-4" data-id="element-1395" data-mp="ait661us8lrp">
                                {study.reason}
                              </p>
                              <div className="bg-white/10 p-4 rounded-lg mb-4 border-l-4 border-blue-400" data-id="element-1396" data-mp="fhcq5wptfnhf">
                                <p className="text-white/90 text-sm italic" data-id="element-1397" data-mp="3izwewp9acb7h">
                                  {study.slug === 'us-bank' && 'A look at how I improved customer satisfaction and team productivity on a Corporate Payment Services team focused on the One Card application at U.S. Bank.'}
                                  {study.slug === 'cyber-security' && 'My first test as a designer on a large platform re-design for a cybersecurity project. I helped transform complex micro segmentation and threat analysis applications into intuitive, user-centered solutions.'}
                                  {study.slug === 'ai-comparison' && 'During our initial research, we explored the capabilities that would be required for an effective conversational AI solution. We conducted a comprehensive analysis of conversational AI platforms, evaluating offerings from IBM Watson, Google Dialogflow, Microsoft, and Amazon.'}
                                </p>
                              </div>
                              <div className="mb-4" data-id="element-1398" data-mp="3on1s7s87v7ul">
                                <h4 className="text-sm uppercase text-white/50 mb-2" data-id="element-1399" data-mp="3onl8l4i7xo6i">
                                  Key Skills Demonstrated:
                                </h4>
                                <div className="flex flex-wrap gap-2" data-id="element-1400" data-mp="1jertghro7p5">
                                  {study.slug === 'us-bank' && <>
                                      <span className="bg-blue-900/50 text-blue-100 px-2 py-1 rounded text-xs" data-id="element-1401" data-mp="3to8z7p2jc307">
                                        UX Design
                                      </span>
                                      <span className="bg-purple-900/50 text-purple-100 px-2 py-1 rounded text-xs" data-id="element-1402" data-mp="18y9qvehkvrx2">
                                        Financial Services
                                      </span>
                                      <span className="bg-green-900/50 text-green-100 px-2 py-1 rounded text-xs" data-id="element-1403" data-mp="16hig5jke56gv">
                                        Customer Experience
                                      </span>
                                    </>}
                                  {study.slug === 'cyber-security' && <>
                                      <span className="bg-blue-900/50 text-blue-100 px-2 py-1 rounded text-xs" data-id="element-1404" data-mp="243pj6p11qqr4">
                                        UI Design
                                      </span>
                                      <span className="bg-red-900/50 text-red-100 px-2 py-1 rounded text-xs" data-id="element-1405" data-mp="1udwhcfqphq02">
                                        Technical Communication
                                      </span>
                                      <span className="bg-yellow-900/50 text-yellow-100 px-2 py-1 rounded text-xs" data-id="element-1406" data-mp="2mwtj2s4uxzu6">
                                        Complex System Simplification
                                      </span>
                                    </>}
                                  {study.slug === 'ai-comparison' && <>
                                      <span className="bg-blue-900/50 text-blue-100 px-2 py-1 rounded text-xs" data-id="element-1407" data-mp="kafza0to2rqn">
                                        AI Strategy
                                      </span>
                                      <span className="bg-indigo-900/50 text-indigo-100 px-2 py-1 rounded text-xs" data-id="element-1408" data-mp="1cybsgvk121io">
                                        Conversational Design
                                      </span>
                                      <span className="bg-teal-900/50 text-teal-100 px-2 py-1 rounded text-xs" data-id="element-1409" data-mp="1380q3dfzs0dw">
                                        Platform Evaluation
                                      </span>
                                    </>}
                                </div>
                              </div>
                            </div>
                            <Link to={`/case-study/${study.slug}`} className="inline-flex items-center text-white font-medium text-sm hover:text-blue-300 transition-colors" data-id="element-1410" data-mp="287fd51x7fgpv">
                              View Full Case Study
                              <ArrowRightIcon className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" data-id="element-1411" data-mp="2i6whng0pzok9" />
                            </Link>
                          </div>
                        </div>
                      </div>
                    </div>)}
                </div>
              </div>
            </div>}
          {/* Browse My Work Section - UPDATED tabs styling */}
          {(viewState !== ViewState.RESULTS || !isJobMode) && <>
              <div id="my-work" className="mb-8 mt-16" data-id="element-1412" data-mp="35vfrhzzf49eg">
                <h2 className="text-2xl sm:text-3xl mb-2 uppercase" style={{
              fontFamily: "'Gefika', sans-serif",
              fontWeight: 400,
              letterSpacing: '0.03em'
            }} data-id="element-1413" data-mp="35wqy07k8zv99">
                  My Work
                </h2>
                <p className="text-white/60 mb-6" style={{
              fontFamily: "'Ronzino', sans-serif",
              fontWeight: 400
            }} data-id="element-1414" data-mp="2g7k0elmoes49">
                  Explore featured projects and case studies
                </p>
              </div>
              <div className="flex border-b border-white/10 mb-8 gap-6" role="tablist" aria-label="Project categories" data-id="element-1415" data-mp="2q46odjaatzai">
                <button onClick={() => handleTabChange('featured')} role="tab" aria-selected={activeTab === 'featured'} aria-controls="featured-panel" className={`pb-2 text-sm font-medium uppercase tracking-wider transition-all ${activeTab === 'featured' ? 'text-white border-b border-white' : 'text-white/40 hover:text-white/80'}`} style={{
              fontFamily: "'Ronzino', sans-serif"
            }} data-id="element-1416" data-mp="33ypjrarts2jb">
                  Featured Work
                </button>
                <button onClick={() => handleTabChange('case-studies')} role="tab" aria-selected={activeTab === 'case-studies'} aria-controls="case-studies-panel" className={`pb-2 text-sm font-medium uppercase tracking-wider transition-all ${activeTab === 'case-studies' ? 'text-white border-b border-white' : 'text-white/40 hover:text-white/80'}`} style={{
              fontFamily: "'Ronzino', sans-serif"
            }} data-id="element-1417" data-mp="1ejj0zk2uypzn">
                  Case Studies
                </button>
              </div>
              <div className="mt-8" data-id="element-1418" data-mp="37d8wve1rgkmx">
                {activeTab === 'featured' && <div id="featured-panel" role="tabpanel" aria-labelledby="featured-tab" className="grid grid-cols-1 gap-8" data-id="element-1419" data-mp="3bgnpd46vyth9">
                    <Link to="/case-study/case-western" className="group block relative overflow-hidden rounded-lg transition-all duration-300 hover:shadow-lg" onMouseEnter={() => window.cursorSelectProject && window.cursorSelectProject()} onMouseLeave={() => window.cursorDefault && window.cursorDefault()} aria-label="View Case Western Reserve University project" data-id="element-1420" data-mp="2xhxoddgy49nm">
                      <div className="relative aspect-w-4 aspect-h-3" data-id="element-1421" data-mp="3jnf63ldp513b">
                        <img src="https://cdn.magicpatterns.com/uploads/r6QmYhNhTgdAoSYhnocL3u/CWRU_COMPV.png" alt="Case Western Reserve University Faculty Information System interface" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" data-id="element-1422" data-mp="3flac2ljc62g5" />
                        <div className="absolute inset-0 bg-black/60 group-hover:bg-black/70 transition-colors" data-id="element-1423" data-mp="35tebn3ts208r"></div>
                        <div className="absolute inset-0 flex flex-col justify-center items-center text-center p-6" data-id="element-1424" data-mp="2vxb1baoruvaq">
                          <p className="text-sm uppercase tracking-wider text-white/70 mb-3" data-id="element-1425" data-mp="2rv7l68r5r2j4">
                            Featured Work
                          </p>
                          <h3 className="text-2xl sm:text-3xl font-bold text-white mb-6" data-id="element-1426" data-mp="2myzcne7gu7kx">
                            CWRU AI-Powered FIS System
                          </h3>
                          <span className="inline-flex items-center text-white font-medium group-hover:underline" data-id="element-1427" data-mp="l5t0b7n29bft">
                            View Project
                            <svg className="ml-2 w-5 h-5 transition-transform group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true" data-id="element-1428" data-mp="xnc4haddq8py">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" data-id="element-1429" data-mp="17doydt733e8r" />
                            </svg>
                          </span>
                        </div>
                      </div>
                    </Link>
                  </div>}
                {activeTab === 'case-studies' && <div id="case-studies-panel" role="tabpanel" aria-labelledby="case-studies-tab" className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1430" data-mp="jnumbdul9tpk">
                    <Link to="/case-study/us-bank" className="group block relative overflow-hidden rounded-lg transition-all duration-300 hover:shadow-lg" onMouseEnter={() => window.cursorSelectProject && window.cursorSelectProject()} onMouseLeave={() => window.cursorDefault && window.cursorDefault()} aria-label="View US Bank case study" data-id="element-1431" data-mp="nnldvjrxwdv5">
                      <div className="relative aspect-w-4 aspect-h-3" data-id="element-1432" data-mp="2v60jfv0mougp">
                        <img src="/usbthumb.png" alt="US Bank Corporate Payment Services interface" className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-110" data-id="element-1433" data-mp="2ggukm85pd69c" />
                        <div className="absolute inset-0 bg-black/60 group-hover:bg-black/70 transition-colors" data-id="element-1434" data-mp="27gfzb8sqnr5u"></div>
                        <div className="absolute inset-0 flex flex-col justify-center items-center text-center p-4" data-id="element-1435" data-mp="2cfj4qucmrzec">
                          <p className="text-xs uppercase tracking-wider text-white/70 mb-1" data-id="element-1436" data-mp="1xja880k4k05m">
                            FINANCIAL SERVICES
                          </p>
                          <h3 className="text-xl font-bold text-white mb-2" data-id="element-1437" data-mp="3rvhyv1pexuvl">
                            US Bank
                          </h3>
                          <span className="inline-flex items-center text-white text-sm font-medium group-hover:underline" data-id="element-1438" data-mp="p6twuha89yz">
                            View Case Study
                          </span>
                        </div>
                      </div>
                    </Link>
                    <Link to="/case-study/cyber-security" className="group block relative overflow-hidden rounded-lg transition-all duration-300 hover:shadow-lg" onMouseEnter={() => window.cursorSelectProject && window.cursorSelectProject()} onMouseLeave={() => window.cursorDefault && window.cursorDefault()} aria-label="View Cyber Security case study" data-id="element-1439" data-mp="1uyc92j5tsdzb">
                      <div className="relative aspect-w-4 aspect-h-3" data-id="element-1440" data-mp="2dweuknz5zssd">
                        <img src="/unisys_thumb.png" alt="Cybersecurity platform interface showing threat analysis" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" data-id="element-1441" data-mp="2duc3e0m9ztm2" />
                        <div className="absolute inset-0 bg-black/60 group-hover:bg-black/70 transition-colors" data-id="element-1442" data-mp="jk8f3cz8mpu9"></div>
                        <div className="absolute inset-0 flex flex-col justify-center items-center text-center p-4" data-id="element-1443" data-mp="2eltr1uzj1b9h">
                          <p className="text-xs uppercase tracking-wider text-white/70 mb-1" data-id="element-1444" data-mp="2jjv8ycl7v4jd">
                            DIGITAL PROTECTION
                          </p>
                          <h3 className="text-xl font-bold text-white mb-2" data-id="element-1445" data-mp="2slbkinnrpcp6">
                            Cyber Security
                          </h3>
                          <span className="inline-flex items-center text-white text-sm font-medium group-hover:underline" data-id="element-1446" data-mp="2xjmtkw68nj6c">
                            View Case Study
                          </span>
                        </div>
                      </div>
                    </Link>
                    <Link to="/case-study/ai-comparison" className="group block relative overflow-hidden rounded-lg transition-all duration-300 hover:shadow-lg" onMouseEnter={() => window.cursorSelectProject && window.cursorSelectProject()} onMouseLeave={() => window.cursorDefault && window.cursorDefault()} aria-label="View AI Comparison case study" data-id="element-1447" data-mp="3barl1azgd55l">
                      <div className="relative aspect-w-4 aspect-h-3" data-id="element-1448" data-mp="3vbktt00nkw33">
                        <img src="/Gemini_Generated_Image_qq6ptsqq6ptsqq6p.png" alt="AI conversational interface comparison visualization" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" data-id="element-1449" data-mp="3qd9l4sm3grmg" />
                        <div className="absolute inset-0 bg-black/60 group-hover:bg-black/70 transition-colors" data-id="element-1450" data-mp="hz16kogeq1da"></div>
                        <div className="absolute inset-0 flex flex-col justify-center items-center text-center p-4" data-id="element-1451" data-mp="czfy9nfckgnw">
                          <p className="text-xs uppercase tracking-wider text-white/70 mb-1" data-id="element-1452" data-mp="8869ep1mhhd2">
                            AI COMPARISON
                          </p>
                          <h3 className="text-xl font-bold text-white mb-2" data-id="element-1453" data-mp="10pa8dx8a7r4g">
                            Watson vs Dialogflow
                          </h3>
                          <span className="inline-flex items-center text-white text-sm font-medium group-hover:underline" data-id="element-1454" data-mp="vx8xaemuo6g0">
                            View Case Study
                          </span>
                        </div>
                      </div>
                    </Link>
                  </div>}
              </div>
            </>}
        </div>
      </section>
      <Footer data-id="element-1455" data-mp="bgxtgfij9k15" />
      <NoMatchModal isOpen={showNoMatchModal} onClose={() => setShowNoMatchModal(false)} searchTerm={noMatchSearchTerm} onSuggestedTopicClick={handleSuggestedTopic} data-id="element-1456" data-mp="bih9c50a1t2e" />
    </div>;
}